/**
 * Device identity — the SDK's one answer to "what device is this client".
 *
 * Precedence (sdk ADR-009):
 *   1. an explicitly pinned `config.deviceId` (e2e seed context, Storybook),
 *      while its `config.deviceAccountId`, when given, is the acting account,
 *   2. the daemon's live id when a daemon is configured — cached with explicit
 *      invalidation, never frozen at construction: the daemon mints a local id at
 *      boot and adopts the backend-canonical one seconds later, so a pinned
 *      pre-adopt value 404s "Device not found" for the life of the window,
 *   3. a per-client id minted in memory on first use — never written anywhere.
 *
 * The per-request `X-Device-Id` header and the registration precondition
 * (sdk ADR-009) apply ONLY to clients that attach this identity — created by
 * `createSDK`. The plain `new LocalAgentSDK(config)` constructor never attaches
 * one, so its behaviour is byte-identical to before this file existed.
 */

import { uuidV4 } from './util/uuid.js';
import type { ShogunChopResource } from './resources/shogunchop.js';
import type { RegisterDeviceRequest } from './types/index.js';

/** How long one daemon `/info` probe may take before the answer is "unavailable". */
const DAEMON_PROBE_TIMEOUT_MS = 2_000;

export class DeviceIdentity {
  private readonly pinnedId: string | undefined;
  private readonly pinnedAccountId: string | undefined;
  private actingAccount: (() => Promise<string>) | null = null;
  private mintedId: string | null = null;
  private daemon: (() => Promise<{ device_id: string }>) | null = null;
  private register: ((req: RegisterDeviceRequest) => Promise<unknown>) | null = null;
  private cachedDaemonId: string | null = null;
  private inflight: Promise<string | null> | null = null;
  private registration: { key: string; promise: Promise<void> } | null = null;

  constructor(pinned: string | undefined, pinnedAccountId?: string) {
    this.pinnedId = pinned;
    this.pinnedAccountId = pinnedAccountId;
  }

  private pinFor(accountId: string | undefined): string | undefined {
    if (this.pinnedId === undefined || this.pinnedAccountId === undefined) return this.pinnedId;
    return accountId === this.pinnedAccountId ? this.pinnedId : undefined;
  }

  private async pinned(): Promise<string | undefined> {
    if (this.pinnedId === undefined || this.pinnedAccountId === undefined) return this.pinnedId;
    if (this.actingAccount === null) {
      throw new Error('DeviceIdentity: a pin bound to an account needs the acting-account source wired');
    }
    return this.pinFor(await this.actingAccount());
  }

  /** Wire the acting account (the current credential's `account_id`) an account-bound pin is checked against. */
  wireActingAccount(source: () => Promise<string>): void {
    this.actingAccount = source;
  }

  /**
   * The web-only minted rung, materialized on FIRST USE rather than at
   * construction (sdk ADR-009 — minting is the last
   * rung, so paying for it eagerly is wrong). The Electron main process
   * constructs a client to probe the daemon and never mints: eager minting
   * dragged Web Crypto into a context that has none.
   */
  private get minted(): string {
    if (this.mintedId === null) {
      this.mintedId = uuidV4();
    }
    return this.mintedId;
  }

  /** Wire the daemon answer and the registration transport once the client exists. */
  wire(daemon: ShogunChopResource | null): void {
    this.daemon = daemon === null ? null : () => daemon.info();
  }

  /** Wire the registration transport (kept off the resource so no resource
   *  method signature changes — the dispatchable surface is untouched). */
  wireRegister(register: (req: RegisterDeviceRequest) => Promise<unknown>): void {
    this.register = register;
  }

  /**
   * The device id this client speaks as. Never null: an explicit pin wins, then
   * the daemon's live answer, then the minted id. A failed daemon probe THROWS
   * here — the caller asked for THE device id, and silently substituting the
   * minted one would bind sessions to an unregistered device that fails further
   * from its cause. `resolveHeader` is the variant that omits instead, for
   * requests that must carry on without naming a device they cannot confirm.
   */
  async resolve(): Promise<string> {
    const pinned = await this.pinned();
    if (pinned) return pinned;
    if (this.daemon !== null) {
      const fromDaemon = await this.probeDaemon();
      if (fromDaemon === null) {
        throw new Error(
          'DeviceIdentity.resolve: the configured daemon could not be asked for its device id — refusing to substitute the minted id'
        );
      }
      return fromDaemon;
    }
    return this.minted;
  }

  /**
   * The `X-Device-Id` header value for one request, or null to omit it.
   *
   * Null means "a daemon is configured but could not be asked within the probe
   * budget" — daemon absence/restart is a legitimate state, not an error, so the
   * request carries on without the header and the next resolution re-asks.
   * An explicit pin or the minted web-only id always answers.
   */
  async resolveHeader(): Promise<string | null> {
    const pinned = await this.pinned();
    if (pinned) return pinned;
    if (this.daemon === null) return this.minted;
    return this.probeDaemon();
  }

  /**
   * The sync projection the credential holder's `bridgeIdentity` reads — the
   * cached daemon answer or the minted web-only id. A key is cache identity;
   * the WIRE value is still resolved per request.
   */
  cachedValue(): string {
    return this.cachedDaemonId ?? this.minted;
  }

  /** Drop the cached daemon answer; the next resolution re-asks. */
  invalidate(): void {
    this.cachedDaemonId = null;
    this.inflight = null;
  }

  /**
   * Guarantee the resolved id is registered against `accountId` before the first
   * authenticated request that uses it — single-flighted on the
   * `(deviceId, accountId)` pair and re-armed when either changes. The
   * registration POST is issued as an `internal` request so it cannot re-enter
   * its own precondition.
   */
  async ensureRegistered(accountId: string): Promise<void> {
    // A daemon-resolved id belongs to the daemon, which registers it with its
    // own name and `desktop` kind. Re-registering it here would overwrite both
    // — the clobber that destroyed device identity on staging
    // (account ADR-036). Only the minted web identity is ours to register.
    if (this.daemon !== null || this.pinFor(accountId) !== undefined) return;
    const deviceId = await this.resolve();
    const key = `${deviceId}:${accountId}`;
    if (this.registration?.key === key) {
      await this.registration.promise;
      return;
    }
    const register = this.register;
    if (register === null) {
      throw new Error('DeviceIdentity.ensureRegistered: registration transport is not wired');
    }
    const request: RegisterDeviceRequest = {
      device_id: deviceId,
      name: 'Browser',
      kind: 'web',
    };
    const promise = register(request)
      .then(() => undefined)
      .catch((err: unknown) => {
        if (this.registration?.key === key) {
          this.registration = null;
        }
        throw err;
      });
    this.registration = { key, promise };
    await promise;
  }

  /**
   * One single-flighted daemon probe. Failure is a legitimate answer (null),
   * never a thrown error — and a failed probe re-arms so the next request asks
   * again instead of pinning the failure.
   */
  private async probeDaemon(): Promise<string | null> {
    if (this.daemon === null) return null;
    if (this.cachedDaemonId !== null) return this.cachedDaemonId;
    if (this.inflight !== null) return this.inflight;
    const probe = (async () => {
      try {
        const info = await Promise.race([
          this.daemon?.() ?? Promise.reject(new Error('no daemon')),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('daemon /info probe timed out')),
              DAEMON_PROBE_TIMEOUT_MS
            )
          ),
        ]);
        this.cachedDaemonId = info.device_id;
        return info.device_id;
      } catch {
        // Daemon absent or slow: omit for this request and re-arm — a restarting
        // daemon must not break reads that need no daemon, and the next request
        // should ask again rather than pinning the failure.
        return null;
      } finally {
        this.inflight = null;
      }
    })();
    this.inflight = probe;
    return probe;
  }
}
